import { v4 } from 'uuid';
import { useReducer, useCallback, useRef, useEffect } from 'react';

export type Action<T> =
  | { type: 'REQUEST'; payload: null | undefined; id: string }
  | { type: 'NEW_REQUEST'; payload: null | undefined; id: string }
  | { type: 'SUCCESS'; payload: T | null; id: string }
  | { type: 'FAILURE'; payload: string; id: string };

export type Status = 'NOT_REQUESTED' | 'LOADING' | 'SUCCESS' | 'ERROR';

export interface State<T> {
  result: T | null;
  error: string | null;
  loading: boolean;
  status: Status;
}

type InternalState<T> = Record<string, State<T>>;

function reducer<T>(state: InternalState<T>, { type, id, payload }: Action<T>): InternalState<T> {
  switch (type) {
    case 'REQUEST':
      return {
        ...state,
        [id]: { ...state[id], loading: true, error: null, status: 'LOADING' },
      };
    case 'NEW_REQUEST':
      return {
        ...state,
        [id]: { ...state[id], loading: false, error: null, status: 'NOT_REQUESTED', result: null },
      };
    case 'SUCCESS':
      return {
        ...state,
        [id]: {
          ...state[id],
          loading: false,
          error: null,
          status: 'SUCCESS',
          result: payload,
        },
      };
    case 'FAILURE':
      return {
        ...state,
        [id]: { ...state[id], loading: false, error: payload, status: 'ERROR' },
      };
    default:
      return state;
  }
}

const INITIAL_STATE: State<never> = {
  status: 'NOT_REQUESTED',
  loading: false,
  result: null,
  error: null,
};

const generateInitialState = (id: string): InternalState<never> => ({ [id]: INITIAL_STATE });

/**
 * Small helper hook to keep the id in sync with provided id when it changes
 * Also just a bit of syntax sugar to avoid doing `idRef.current` everywhere
 */
const useConsistentId = (providedId?: string): string => {
  const idRef = useRef<string>(providedId ?? v4());

  useEffect(() => {
    if (providedId) {
      idRef.current = providedId;
    }
  }, [providedId]);

  return idRef.current;
};

type Awaited<T> = T extends PromiseLike<infer U> ? Awaited<U> : T;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyFunction = (...args: any[]) => any;

/**
 * This is just an alternative to `useAsyncResponse` which does not require us to make use of recoil
 *
 * Given that our services are managing state internally we can't make use of `useAsyncResponse`
 * because we would be managing the same state in two different parts of our application
 *
 */
export function useAsyncFn<Fn extends AnyFunction, TReturn extends ReturnType<Fn>>(
  asyncCallback: Fn,
  providedId?: string,
): [State<Awaited<TReturn>>, (...args: Parameters<Fn>) => ReturnType<Fn>] {
  const mountedRef = useRef<boolean>(true);

  // this is to keep the currentId consistent, even if we don't pass it in
  // as well as updating the id if it changes
  const id = useConsistentId(providedId);

  const [state, dispatch] = useReducer(reducer, generateInitialState(id));

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const dispatchIfMounted = (action: Action<unknown>) => {
    if (mountedRef.current === true) dispatch(action);
  };

  useEffect(() => {
    if (providedId) {
      dispatchIfMounted({ type: 'NEW_REQUEST', id: providedId, payload: null });
    }
  }, [providedId]);

  const execute = useCallback(
    // eslint-disable-next-line consistent-return
    async (...args: Parameters<Fn>) => {
      dispatchIfMounted({ type: 'REQUEST', id, payload: null });
      try {
        const result = await asyncCallback(...args);

        dispatchIfMounted({ type: 'SUCCESS', payload: result, id });

        return result;
      } catch (error) {
        if (error instanceof Error) {
          dispatchIfMounted({ type: 'FAILURE', payload: error.message, id });
        } else {
          dispatchIfMounted({ type: 'FAILURE', payload: error as string, id });
        }
      }
    },
    [asyncCallback, id],
  );

  return [state[id] as State<Awaited<TReturn>>, execute as Fn];
}
